import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Card, CardBody, CardFooter, Divider } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useCreateCustomer, checkCustomerEmail, checkCustomerPhone,
} from '@/api/customers';
import { useMe } from '@/api/me';
import {
  wizardSchema, defaultValues, stepFields, buildPayload,
  type CustomerWizardValues,
} from './wizard-form';
import { WizardStepper, WIZARD_STEPS } from './wizard-stepper';
import { useExistenceCheck } from './use-existence-check';
import { StepCheckEmail } from './step-check-email';
import { StepBusinessInfo } from './step-business-info';
import { StepContactInfo } from './step-contact-info';
import { StepAdditionalInfo } from './step-additional-info';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LAST_STEP = WIZARD_STEPS.length - 1;

/**
 * `/customers/new` — 4-step create wizard (Check email → Business info →
 * Contact info → Additional info). Mirrors the datacentral-client-v4 flow:
 * per-step validation gates Continue, email/phone existence is checked
 * (debounced) against the marketing/customer pools, and the final step submits
 * `POST /customers`. Functionality parity with legacy; UX rebuilt on @ui idioms
 * (Stepper + Card + Field). Async company/product/employee selects are deferred.
 */
export default function CustomerNewPage() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const create = useCreateCustomer();
  const [step, setStep] = useState(0);
  const [reached, setReached] = useState(0);
  const [submitError, setSubmitError] = useState('');

  const methods = useForm<CustomerWizardValues>({
    resolver: yupResolver(wizardSchema),
    mode: 'onChange',
    defaultValues: defaultValues(),
  });

  const { control, trigger, getValues, setValue } = methods;

  // Seed the sales owner with the signed-in employee once `/auth/me` resolves,
  // without clobbering any edits the user has already made to that field.
  const seededFollower = useRef(false);
  const meId = me?.employee?.employee_id;
  useEffect(() => {
    if (seededFollower.current || !meId) return;
    const current = getValues('customer.customer_sale_followers');
    if (!current || current.length === 0) {
      setValue('customer.customer_sale_followers', [meId], { shouldValidate: false });
    }
    seededFollower.current = true;
  }, [meId, getValues, setValue]);
  const emailValue = useWatch({ control, name: 'customer.customer_email' });
  const phoneValue = useWatch({ control, name: 'customer.customer_phone' });
  const customerType = useWatch({ control, name: 'customer.customer_type' });

  const email = useExistenceCheck(
    emailValue,
    (v) => EMAIL_RE.test(v),
    checkCustomerEmail,
  );
  const phone = useExistenceCheck(
    phoneValue,
    (v) => v.length >= 8,
    checkCustomerPhone,
  );

  const emailBlocked =
    !EMAIL_RE.test((emailValue || '').trim()) || email.checking || !!email.result?.exists;
  const phoneBlocked = !!phone.result?.exists_in_contact_sources;

  const goNext = async () => {
    setSubmitError('');
    if (step === 0) {
      const ok = await trigger('customer.customer_email');
      if (!ok || emailBlocked) return;
    } else {
      const fields = stepFields(step, customerType === 'company');
      const results = await Promise.all(fields.map((f) => trigger(f as never)));
      if (!results.every(Boolean)) return;
      if (step === 2 && phoneBlocked) return;
    }

    if (step === LAST_STEP) {
      try {
        const res = await create.mutateAsync(buildPayload(getValues()));
        const id = res?.result?.customer_id;
        navigate(id ? `/customers/${id}` : '/customers');
      } catch (e) {
        setSubmitError(e instanceof Error ? e.message : 'Failed to create customer. Please try again.');
      }
      return;
    }

    const next = step + 1;
    setStep(next);
    setReached((r) => Math.max(r, next));
  };

  const goBack = () => {
    setSubmitError('');
    setStep((s) => Math.max(0, s - 1));
  };

  const continueDisabled =
    create.isPending ||
    (step === 0 && emailBlocked) ||
    (step === 2 && phoneBlocked);

  return (
    <Container size="lg">
      <PageHeader
        title="New customer"
        description="Register a new customer in four quick steps."
        actions={
          <Button variant="ghost" startIcon={<ArrowLeft size={15} />} onClick={() => navigate('/customers')}>
            Back to customers
          </Button>
        }
      />

      <FormProvider {...methods}>
        <Card>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
            <WizardStepper
              current={step}
              reached={reached}
              onStepClick={(i) => i <= reached && setStep(i)}
            />
            <Divider />

            {step === 0 ? <StepCheckEmail email={email} /> : null}
            {step === 1 ? <StepBusinessInfo /> : null}
            {step === 2 ? <StepContactInfo phone={phone} /> : null}
            {step === 3 ? <StepAdditionalInfo /> : null}

            {submitError ? <Alert tone="danger" title={submitError} /> : null}
          </CardBody>

          <CardFooter style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-3)' }}>
            <Button
              variant="secondary"
              startIcon={<ArrowLeft size={15} />}
              disabled={step === 0 || create.isPending}
              onClick={goBack}
            >
              Back
            </Button>
            <Button
              onClick={goNext}
              loading={create.isPending}
              disabled={continueDisabled}
              startIcon={step === LAST_STEP ? <Check size={15} /> : undefined}
              endIcon={step === LAST_STEP ? undefined : <ArrowRight size={15} />}
            >
              {step === LAST_STEP ? 'Create customer' : 'Continue'}
            </Button>
          </CardFooter>
        </Card>
      </FormProvider>
    </Container>
  );
}
